import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import animeConfig from '../config/animeConfig';
import { trackChibiDashClick, trackHomeRankings } from '../services/analyticsService';
import { getGrandMasters, getReigns } from '../services/leaderboardService';
import Avatar from '../components/Avatar/Avatar';
import FeedbackForm from '../components/FeedbackForm/FeedbackForm';
import quizStats from '../data/quizStats';
import './QuizHome.css';

const QuizHome = () => {
  const navigate = useNavigate();
  const config = animeConfig.onePiece;

  // A short leaderboard on the homepage — only 11 of 153 visitors opened the
  // full one last month. Decorative: it hides itself if the call fails.
  const [topPirates, setTopPirates] = useState([]);
  // Who is holding #1 right now, and for how long. Worked out from the plays
  // themselves on every load, so it needs no upkeep.
  const [reign, setReign] = useState(null);

  useEffect(() => {
    let live = true;
    getGrandMasters()
      .then((masters) => {
        if (!live || !masters || masters.length === 0) return;
        setTopPirates(masters.slice(0, 5));
        trackHomeRankings('shown');
      })
      .catch(() => {});
    getReigns('grand-masters')
      .then((r) => { if (live && r) setReign(r); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  // One source for both the visible FAQ and the FAQPage JSON-LD, so the two can
  // never drift apart. Answers that quote scores read them from quizStats, which
  // is regenerated from the live leaderboard on every deploy — a hardcoded
  // "nobody has done it yet" goes stale the day somebody does.
  const byKey = Object.fromEntries(quizStats.games.map((g) => [g.key, g]));
  // hardestKey ignores games with too few plays to mean anything — see the
  // MIN_SAMPLE note in scripts/build-quiz-stats.js. We never name an "easiest"
  // game: telling players one of the four is the soft option just steers them
  // away from it.
  const hardest = byKey[quizStats.hardestKey];

  const faqs = [
    {
      q: 'Which quiz is the hardest?',
      a:
        `The ${hardest.label} — players average ${hardest.average} out of ${hardest.outOf} and the best ` +
        `score anyone has posted is ${hardest.best}. Naming a face out of a cast of hundreds is harder ` +
        `than matching a fruit to its power. Averages and best-ever scores for all four games are on ` +
        `the quiz stats page.`,
    },
    {
      q: 'How do I get my score on the leaderboard?',
      a:
        'Finish a round and type a pirate name when it asks. That is the whole thing — one tap and ' +
        'the score is saved. You can set a password later if you want the name reserved on another device.',
    },
    {
      q: 'Where do the questions come from?',
      a:
        'Character and image questions are built from the MyAnimeList character database, so the ' +
        'faces are the official artwork rather than fan drawings. Devil Fruit and bounty questions ' +
        'come from the series itself.',
    },
    {
      q: 'How do I become a Legend?',
      a:
        `Score 11 or higher in all four games — Character, Image, Devil Fruit and Bounty Battle. ` +
        (quizStats.legendCount === 0
          ? 'Nobody has done it yet. '
          : `${quizStats.legendCount} player${quizStats.legendCount === 1 ? ' has' : 's have'} done it. `) +
        `The ${byKey.character ? byKey.character.label : 'Character Quiz'} is where most runs end — ` +
        `players tend to arrive with three legs done and stall on putting names to faces.`,
    },
  ];

  const chibiDashGames = [
    { name: 'One Piece', url: 'https://www.chibidash.com/games/another-one-piece', image: '/chibidash-onepiece.png' },
    { name: 'Naruto', url: 'https://www.chibidash.com/games/naruto', image: '/chibidash-naruto.png' },
    { name: 'Bleach', url: 'https://www.chibidash.com/games/bleach', image: '/chibidash-bleach.png' },
    { name: 'One Punch', url: 'https://www.chibidash.com/games/one-punch', image: '/chibidash-onepunch.png' },
    { name: 'Dragon Ball', url: 'https://www.chibidash.com/games/dragon-ball', image: '/chibidash-dragonball.jpg' },
  ];

  const handleChibiDashClick = (animeName, url) => {
    trackChibiDashClick(animeName);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const games = [
    {
      id: 'character',
      title: 'Character Quiz',
      description: 'Guess One Piece characters by their image. 15 questions, beat the timer!',
      path: '/quiz',
      icon: '/character-quiz-and-any-related.png'
    },
    {
      id: 'image',
      title: 'Image Quiz',
      description: 'Match character names to their images. Test your visual memory!',
      path: '/image-quiz',
      icon: '/image-quiz-and-any-related.png'
    },
    {
      id: 'devil-fruit',
      title: 'Devil Fruit Quiz',
      description: 'Match Devil Fruits to their users. How well do you know the powers?',
      path: '/devil-fruit-quiz',
      icon: '/devil-fruit-quiz-and-any-related.png'
    },
    {
      id: 'bounty-battle',
      title: 'Bounty Battle',
      description: 'Which pirate has the higher bounty? 11 rounds, beat the clock!',
      path: '/bounty-battle',
      emoji: '💰'
    },
    {
      id: 'puzzle',
      title: 'Jigsaw Puzzle',
      description: 'Put Luffy, Shanks and 22 more back together. 12, 30 or 64 pieces.',
      path: '/puzzle',
      emoji: '🧩',
      offBoard: true
    },
    {
      id: 'which-character',
      title: 'Which Character Are You?',
      description: 'Eight questions about how you fight and what you want. One character comes out.',
      path: '/which-character',
      emoji: '❓',
      offBoard: true
    },
    {
      id: 'crew',
      title: 'Build Your Crew',
      description: 'Pick up to ten pirates, see what your crew is worth, and keep the card.',
      path: '/crew',
      emoji: '🏴‍☠️'
    },
    {
      id: 'wanted',
      title: 'WANTED Poster Maker',
      description: 'Put your own photo on a Marine wanted poster and download it.',
      path: '/wanted',
      emoji: '📜'
    }
  ];

  return (
    <>
      <Helmet>
        <title>One Piece Quiz — 4 Free Trivia Games with a Leaderboard</title>
        <meta name="description" content="Four free One Piece quiz games: name the character, match the image, name the Devil Fruit user, and guess the higher bounty. 15 questions, then see where you rank." />
        <link rel="canonical" href="https://www.onepiecetrivia.com/" />
        <meta property="og:title" content="One Piece Quiz — 4 Free Trivia Games with a Leaderboard" />
        <meta property="og:description" content="Play free One Piece quiz games. Guess characters, match images, and name Devil Fruits. 15 questions per round with a leaderboard." />
        <meta property="og:url" content="https://www.onepiecetrivia.com/" />
        <meta property="og:image" content="https://www.onepiecetrivia.com/logo512.png" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta name="twitter:title" content="One Piece Quiz — 4 Free Trivia Games with a Leaderboard" />
        <meta name="twitter:description" content="Play free One Piece quiz games. Guess characters, match images, and name Devil Fruits. 15 questions per round with a leaderboard." />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'WebSite',
              '@id': 'https://www.onepiecetrivia.com/#website',
              url: 'https://www.onepiecetrivia.com/',
              name: 'One Piece Trivia',
              description: 'Free One Piece quiz games — guess characters, match images, name Devil Fruits, and battle bounties.'
            },
            {
              '@type': 'WebApplication',
              name: 'One Piece Trivia',
              url: 'https://www.onepiecetrivia.com/',
              applicationCategory: 'GameApplication',
              operatingSystem: 'Any (web browser)',
              offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' }
            },
            {
              '@type': 'ItemList',
              name: 'One Piece Quiz Games',
              itemListElement: games.map((g, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                name: g.title,
                url: `https://www.onepiecetrivia.com${g.path}`
              }))
            },
            {
              '@type': 'FAQPage',
              mainEntity: faqs.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a }
              }))
            }
          ]
        })}</script>
      </Helmet>
      <div
        className="quiz-home-container"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.9)), url(${config.backgroundImage})`,
        }}
      >
        <div className="quiz-home-content">
          <h1 className="quiz-home-title">CHOOSE YOUR CHALLENGE</h1>

          <div className="quiz-home-image-container">
            <img src={config.introImage} alt="One Piece Quiz" className="quiz-home-intro-image" />
          </div>

          <p className="quiz-home-subtitle">Test your One Piece knowledge</p>

          {topPirates.length > 0 && (
            <div className="mini-board">
              <div className="mini-board-head">
                <span className="mini-board-title">⚔️ Pirate Rankings ⚔️</span>
                <span className="mini-board-sub">Best total across all four games</span>
              </div>
              {reign && (
                <div className="mini-reign">
                  <span className="mini-reign-crown" aria-hidden="true">👑</span>
                  <span className="mini-reign-name">{reign.current.username}</span>
                  <span className="mini-reign-days">
                    #1 for {reign.current.days} day{reign.current.days === 1 ? '' : 's'}
                  </span>
                </div>
              )}

              <div className="mini-board-list">
                {topPirates.map((p, i) => (
                  <div key={p.username} className={`mini-board-row ${i < 3 ? `mini-top-${i + 1}` : ''}`}>
                    {i === 0 && <span className="mini-board-glow" />}
                    <span className="mini-board-rank">
                      {['🥇', '🥈', '🥉'][i] || i + 1}
                    </span>
                    <Avatar value={p.avatar} className="mini-board-avatar" alt="" />
                    <span className="mini-board-name">
                      {p.username}
                      <span className="mini-board-pill">🏴‍☠️</span>
                    </span>
                    <span className="mini-board-score">
                      {p.totalScore}<span className="mini-board-max">/56</span>
                    </span>
                  </div>
                ))}
              </div>
              <Link
                to="/leaderboard"
                className="mini-board-cta"
                onClick={() => trackHomeRankings('click')}
              >
                See full rankings for others
              </Link>
            </div>
          )}

          <div className="quiz-cards-container">
            {games.map((game) => (
              <div key={game.id} className="quiz-card">
                {game.emoji ? (
                  <span className="quiz-card-icon quiz-card-emoji" role="img" aria-label={game.title}>{game.emoji}</span>
                ) : (
                  <img src={game.icon} alt={game.title} className="quiz-card-icon" />
                )}
                <h2 className="quiz-card-title">{game.title}</h2>
                {game.offBoard && <span className="quiz-card-tag">Not on the leaderboard yet</span>}
                <p className="quiz-card-description">{game.description}</p>
                <button
                  className="quiz-card-button"
                  onClick={() => navigate(game.path)}
                >
                  Play Now
                </button>
              </div>
            ))}
          </div>

          <div className="chibidash-promo">
            <h2 className="chibidash-title">More Anime Games</h2>
            <p className="chibidash-subtitle">Love anime quizzes? Try all 5 on ChibiDash</p>
            <div className="chibidash-games">
              {chibiDashGames.map((game) => (
                <button
                  key={game.name}
                  className="chibidash-game-card"
                  onClick={() => handleChibiDashClick(game.name, game.url)}
                >
                  <div className="chibidash-img-wrapper">
                    <img src={game.image} alt={game.name} className="chibidash-game-img" />
                  </div>
                  <span className="chibidash-game-name">{game.name}</span>
                </button>
              ))}
            </div>
            <a
              href="https://www.chibidash.com/games"
              target="_blank"
              rel="noopener noreferrer"
              className="chibidash-link"
              onClick={() => trackChibiDashClick('main_link')}
            >
              Visit ChibiDash.com
            </a>
          </div>

          <FeedbackForm />

          <section className="seo-content">
            <h2>One Piece Trivia: Free Online Quiz Games for Fans</h2>
            <p>
              You think you know One Piece? <strong>One Piece Trivia</strong> is a free collection of
              quiz games that test how well you really know Luffy, the Straw Hat crew, and the
              whole world of One Piece. Guess characters from a single image, match names to faces in the
              Image Quiz, name the user behind every Devil Fruit, and prove your knowledge of pirate
              bounties in Bounty Battle.
            </p>
            <p>
              Every round is timed with 15 questions, and your best scores can be saved to a global{' '}
              <Link to="/leaderboard">leaderboard</Link>. Score 11 or higher in all four games to earn the
              coveted <strong>Legend</strong> rank
              {quizStats.legendCount === 0
                ? ' — nobody has managed it yet.'
                : quizStats.legendCount === 1
                ? ' — exactly one player has managed it so far.'
                : ` — only ${quizStats.legendCount} players have managed it so far.`}
            </p>

            <h2>Explore the One Piece World</h2>
            <ul className="seo-links">
              <li><Link to="/quiz">Character Quiz</Link> — guess the character from their image</li>
              <li><Link to="/image-quiz">Image Quiz</Link> — match names to faces</li>
              <li><Link to="/devil-fruit-quiz">Devil Fruit Quiz</Link> — name every fruit's user</li>
              <li><Link to="/bounty-battle">Bounty Battle</Link> — who has the higher bounty?</li>
              <li><Link to="/crew">Build Your Crew</Link> — pick your pirates and download the card</li>
              <li><Link to="/wanted">WANTED Poster Maker</Link> — your own photo on a bounty poster</li>
              <li><Link to="/which-character">Which Character Are You?</Link> — eight questions, one result</li>
              <li><Link to="/characters">Character Encyclopedia</Link> — browse profiles and stats</li>
              <li><Link to="/arcs">Story Arcs</Link> — the full One Piece timeline</li>
              <li><Link to="/guides">Guides</Link> — Straw Hat crew, Devil Fruits and more</li>
              <li><Link to="/leaderboard">Leaderboard</Link> — see the top players</li>
            </ul>

            <h2>Questions Players Actually Ask</h2>
            <div className="seo-faq">
              {faqs.map((f) => (
                <React.Fragment key={f.q}>
                  <h3>{f.q}</h3>
                  <p>{f.a}</p>
                </React.Fragment>
              ))}
              <p className="seo-faq-more">
                Full difficulty breakdown, with every average and best-ever score:{' '}
                <Link to="/quiz-stats">how hard is the One Piece quiz?</Link>
              </p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default QuizHome;
